import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NAV_LINKS, CONTACT_INFO } from '../../data/mock';
import { Menu, X, Phone, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'mx-4 mt-3 rounded-full border border-black/10 bg-white/70 backdrop-blur-2xl shadow-sm'
            : 'mx-4 mt-3 rounded-full bg-white/50 backdrop-blur-xl'
        } lg:mx-6`}
      >
        <div className="flex items-center justify-between px-4 py-2 lg:px-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 no-underline shrink-0">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] flex items-center justify-center">
              <ArrowRight className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-sm lg:text-base text-[rgb(0,55,32)] whitespace-nowrap">
              Beginning to End Tax
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 no-underline ${
                  location.pathname === link.path
                    ? 'bg-black/5 text-[rgb(0,55,32)]'
                    : 'text-[rgb(131,146,140)] hover:text-[rgb(0,55,32)] hover:bg-black/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${CONTACT_INFO.phone}`}
              className="flex items-center gap-1.5 text-sm text-[rgb(131,146,140)] hover:text-[rgb(0,55,32)] transition-colors no-underline"
            >
              <Phone className="w-3.5 h-3.5" />
              {CONTACT_INFO.phone}
            </a>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] text-white font-semibold text-sm shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 no-underline"
            >
              Book Free Consult
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-full hover:bg-black/5 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5 text-[rgb(0,55,32)]" /> : <Menu className="w-5 h-5 text-[rgb(0,55,32)]" />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-0 top-16 z-40 mx-4 mt-2 rounded-2xl bg-white/95 backdrop-blur-2xl border border-black/10 shadow-lg p-4 lg:hidden"
          >
            <nav className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors no-underline ${
                    location.pathname === link.path
                      ? 'bg-[rgba(148,242,127,0.1)] text-[rgb(0,55,32)]'
                      : 'text-[rgb(131,146,140)] hover:bg-black/5 hover:text-[rgb(0,55,32)]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <div className="mt-3 pt-3 border-t border-black/10 flex flex-col gap-2">
              <a
                href={`tel:${CONTACT_INFO.phone}`}
                className="flex items-center gap-2 px-4 py-2 text-sm text-[rgb(131,146,140)] no-underline"
              >
                <Phone className="w-4 h-4" />
                {CONTACT_INFO.phone}
              </a>
              <Link
                to="/book"
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] text-white font-semibold text-sm no-underline"
              >
                Book Free Consult
              </Link>
              <Link
                to="/start-here"
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-black/10 text-[rgb(0,55,32)] font-medium text-sm no-underline hover:bg-black/5 transition-colors"
              >
                Start Here
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
